import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Check, ArrowUpRight } from "lucide-react";
import { useInquiryCart } from "@/context/InquiryCartContext";
import { brandName, categoryName, subcategoryName } from "@/lib/labels";

export default function ProductCard({ product, index = 0 }) {
  const { addItem, has } = useInquiryCart();
  const inCart = has(product.id);
  const [justAdded, setJustAdded] = useState(false);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      productId: product.id,
      name: product.name,
      brand: product.brand,
      category: product.category,
      slug: product.slug,
      image: product.images?.[0],
    });
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1600);
    toast.success("Added to Inquiry Cart", {
      description: product.name,
    });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col bg-white border border-border rounded-lg overflow-hidden hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] hover:border-aqua/40 transition-all duration-300"
    >
      <Link to={`/products/${product.slug}`} className="block relative overflow-hidden bg-[#f6f8f9] aspect-square p-5">
        <img
          src={product.images?.[0]}
          alt={product.name}
          loading="lazy"
          // Whole product visible, never cropped — see the gallery comment.
          className="w-full h-full object-contain transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 text-[0.62rem] font-bold uppercase tracking-widest bg-white/90 backdrop-blur px-2.5 py-1 rounded text-greyish">
          {brandName(product.brand)}
        </span>
        {product.featured && (
          <span className="absolute top-3 right-3 text-[0.62rem] font-bold uppercase tracking-widest bg-aqua text-white px-2.5 py-1 rounded">
            Featured
          </span>
        )}
      </Link>

      <div className="flex flex-col flex-1 p-5">
        <p className="text-[0.68rem] uppercase tracking-widest text-greyish font-semibold">
          {categoryName(product.category)}
          {product.subcategory ? ` · ${subcategoryName(product.subcategory)}` : ""}
        </p>
        <Link to={`/products/${product.slug}`} className="mt-1.5">
          <h3 className="text-lg font-bold text-ink leading-snug group-hover:text-aqua transition-colors">
            {product.name}
          </h3>
        </Link>
        <p className="mt-2 text-sm text-greyish line-clamp-2 flex-1">{product.shortDescription}</p>

        <div className="mt-5 flex items-center gap-2">
          <Link
            to={`/products/${product.slug}`}
            data-testid={`view-product-${product.slug}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 border border-border text-ink text-sm font-semibold px-4 py-2.5 rounded-md hover:border-ink/40 hover:bg-[#f6f8f9] transition-all"
          >
            View Details <ArrowUpRight className="w-4 h-4" />
          </Link>
          <button
            onClick={handleAdd}
            data-testid={`add-to-inquiry-${product.slug}`}
            aria-label={`Add ${product.name} to inquiry`}
            className={`inline-flex items-center justify-center w-11 h-11 rounded-md transition-all duration-300 ${
              justAdded || inCart
                ? "bg-aqua text-white"
                : "bg-ink text-white hover:bg-aqua"
            }`}
          >
            {justAdded ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </motion.article>
  );
}
